import type { MetaFunction } from "react-router";
import { Box, Container, Typography, Card, Stack, Button, Divider, Grid } from '@mui/material';
import { useState } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

const officePhotos = [
  '/img/kancelar/IMG_20250929_171944.webp',
  '/img/kancelar/IMG_20250929_172002.webp',
];

export const meta: MetaFunction = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Kontakt — Mgr. Hana Jarkovská",
    "description": "Kontaktní informace pro objednání konzultace psychoterapie nebo etopedického poradenství.",
    "url": "https://www.hanajarkovska.cz/kontakt",
    "mainEntity": {
      "@type": "LocalBusiness",
      "name": "Mgr. Hana Jarkovská - Psychoterapie a etopedické poradenství",
      "telephone": "+420797677397",
      "email": "hana.jarkovska@etoped.cz",
      "image": "https://www.hanajarkovska.cz/img/kancelar/IMG_20250929_171944.webp",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Bílovec",
        "postalCode": "743 01",
        "addressCountry": "CZ"
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "18:00"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+420797677397",
        "email": "hana.jarkovska@etoped.cz",
        "contactType": "customer service",
        "availableLanguage": "Czech"
      }
    }
  };

  return [
    { tagName: "link", rel: "canonical", href: "https://www.hanajarkovska.cz/kontakt" },
    { title: "Kontakt — Mgr. Hana Jarkovská" },
    { name: "description", content: "Domluvte si konzultaci. Telefon: 797 677 397, E-mail: hana.jarkovska@etoped.cz" },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://www.hanajarkovska.cz/kontakt" },
    { property: "og:title", content: "Kontakt — Mgr. Hana Jarkovská" },
    { property: "og:description", content: "Domluvte si konzultaci. Telefon: 797 677 397, E-mail: hana.jarkovska@etoped.cz" },
    { property: "og:image", content: "https://www.hanajarkovska.cz/img/kancelar/IMG_20250929_171944.webp" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "Kontakt — Mgr. Hana Jarkovská" },
    { name: "twitter:description", content: "Domluvte si konzultaci. Telefon: 797 677 397, E-mail: hana.jarkovska@etoped.cz" },
    { name: "twitter:image", content: "https://www.hanajarkovska.cz/img/kancelar/IMG_20250929_171944.webp" },
    { "script:ld+json": structuredData },
  ];
};

export default function ContactPage() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <>
      <Box sx={{ bgcolor: 'background.default', py: { xs: 6, md: 12 } }}>
        <Container maxWidth="md">
          <Typography variant="h1" sx={{ mb: 2, textAlign: 'center' }}>
            Kontakt
          </Typography>

          <Typography
            sx={{
              fontSize: '1.1rem',
              lineHeight: 1.7,
              mb: 6,
              textAlign: 'center',
              color: 'text.secondary',
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            Pokud máte zájem o konzultaci nebo více informací, neváhejte mě kontaktovat.
            Ráda si s vámi domluvím schůzku, online nebo osobně.
          </Typography>

          <Card
            sx={{
              p: { xs: 3, md: 5 },
              mb: 4,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              '&:hover': {
                transform: 'none',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              },
            }}
          >
            <Stack spacing={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: 'primary.contrastText',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    mb: 1.5,
                  }}
                >
                  Telefon
                </Typography>

                <Typography
                  component="a"
                  href="tel:+420797677397"
                  sx={{
                    fontSize: { xs: '1.5rem', md: '1.75rem' },
                    fontWeight: 500,
                    color: 'text.primary',
                    textDecoration: 'none',
                    display: 'block',
                    transition: 'color 0.2s',
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  +420&nbsp;797&nbsp;677&nbsp;397
                </Typography>
              </Box>

              <Divider sx={{ opacity: 0.4 }} />

              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: 'primary.contrastText',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    mb: 1.5,
                  }}
                >
                  E-mail
                </Typography>

                <Typography
                  component="a"
                  href="mailto:hana.jarkovska@etoped.cz"
                  sx={{
                    fontSize: { xs: '1.15rem', md: '1.35rem' },
                    fontWeight: 500,
                    color: 'text.primary',
                    textDecoration: 'none',
                    display: 'block',
                    transition: 'color 0.2s',
                    wordBreak: 'break-word',
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  hana.jarkovska@etoped.cz
                </Typography>
              </Box>

              <Box sx={{ textAlign: 'center', pt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  href="mailto:hana.jarkovska@etoped.cz"
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 500,
                    borderRadius: 2,
                  }}
                >
                  Domluvit konzultaci
                </Button>
              </Box>
            </Stack>
          </Card>

          <Typography
            sx={{
              textAlign: 'center',
              color: 'text.secondary',
              fontSize: '0.95rem',
              lineHeight: 1.8,
              maxWidth: '500px',
              mx: 'auto',
            }}
          >
            Individuální konzultace probíhají po domluvě. V případě e-mailu se vám ozvu
            do několika dnů.
          </Typography>

          <Box sx={{ mt: 6, mb: 4 }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: '1.3rem',
                fontWeight: 500,
                textAlign: 'center',
                mb: 3,
              }}
            >
              Konzultace probíhají v bezpečném a klidném místě pro setkávání v Bílovci.
            </Typography>

            <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
              {officePhotos.map((photo, index) => (
                <Grid size={{ xs: 12, sm: 6, md: 5 }} key={index}>
                  <Box
                    onClick={() => openLightbox(index)}
                    sx={{
                      position: 'relative',
                      paddingBottom: '75%',
                      overflow: 'hidden',
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={photo}
                      alt={index === 0 ? "Poradenská místnost v Bílovci - klidný prostor pro psychoterapii a etopedické konzultace" : "Terapeutická kancelář Bílovec - útulné prostředí pro konzultace"}
                      loading="lazy"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>

      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={officePhotos.map((photo) => ({ src: photo }))}
      />
    </>
  );
}
